// Visual paint-condition picker for the permanent-flow paintCondition
// axis. Two tiles: Good condition (no primer needed) vs Has issues
// (primer included). Same shape as the size + sprayer galleries so the
// build flow reads as one consistent visual language.

import { useState } from "react"

interface PaintConditionChip {
  value: string
  label: string
  helperText?: string
}

interface Props {
  chips: PaintConditionChip[]
  priceDelta?: Record<string, number>
  onSelect: (chipLabel: string) => void
}

const PAINT_CONDITION_IMAGE_URLS: Record<string, string> = {
  good: "https://cdn.shopify.com/s/files/1/0973/6008/files/No_Primer_b8f16eed-b049-4b52-ab6e-dd8f8de663c0.webp",
  "has-issues": "https://cdn.shopify.com/s/files/1/0973/6008/files/Primer_da3b0695-b423-45bf-b073-2bb21a457ec4.webp",
}

function imageUrlFor(value: string): string | null {
  return PAINT_CONDITION_IMAGE_URLS[value.trim().toLowerCase()] ?? null
}

export function PaintConditionGallery({ chips, priceDelta, onSelect }: Props) {
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          opacity: 0.6,
          marginBottom: 10,
          textAlign: "center",
        }}
      >
        Tap your paint condition
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
        }}
      >
        {chips.map((c) => {
          const imageUrl = imageUrlFor(c.value)
          const delta = priceDelta?.[c.value]
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => onSelect(c.label)}
              title={c.helperText}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 8,
                padding: "10px 10px 8px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "inherit",
                cursor: "pointer",
                font: "inherit",
                textAlign: "left",
                transition:
                  "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(109, 195, 247, 0.12)"
                e.currentTarget.style.borderColor = "rgba(109, 195, 247, 0.55)"
                e.currentTarget.style.boxShadow =
                  "0 0 0 3px rgba(109,195,247,0.18)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.05)"
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)"
                e.currentTarget.style.boxShadow = "none"
              }}
            >
              <div
                style={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  background:
                    "radial-gradient(120% 100% at 50% 0%, #ffffff 0%, #f3f5f8 70%, #e6eaf0 100%)",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 8,
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6)",
                  overflow: "hidden",
                }}
              >
                <PaintConditionImageOrPlaceholder
                  imageUrl={imageUrl}
                  label={c.label}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13 }}>{c.label}</span>
                {delta != null && (
                  <span style={{ opacity: 0.7, fontSize: 11.5 }}>
                    +${(delta / 100).toFixed(0)}
                  </span>
                )}
              </div>
              {c.helperText && (
                <span
                  style={{ opacity: 0.65, fontSize: 11.5, lineHeight: 1.3 }}
                >
                  {c.helperText}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function PaintConditionImageOrPlaceholder({
  imageUrl,
  label,
}: {
  imageUrl: string | null
  label: string
}) {
  const [failed, setFailed] = useState(false)
  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt={label}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          display: "block",
        }}
      />
    )
  }
  return (
    <svg
      viewBox="0 0 64 64"
      style={{ width: "55%", height: "auto", color: "#2b3a55" }}
      aria-hidden="true"
    >
      <circle cx="32" cy="32" r="22" fill="none" stroke="currentColor" strokeWidth="3" />
      <text
        x="32"
        y="38"
        textAnchor="middle"
        fontSize="20"
        fontWeight="600"
        fill="currentColor"
      >
        ?
      </text>
    </svg>
  )
}
